import { SolIcon } from '@sol/icons';
import { ObjectTools } from '../../tools/object-tools';

export enum SolButtonType {
  primary = 'primary',
  secondary = 'secondary',
  transparent = 'transparent',
  accent = 'accent',
  danger = 'danger',
  link = 'link',
  dashed = 'dashed',
}

export enum SolButtonMode {
  text = 'text',
  icon = 'icon',
  custom = 'custom',
}

export enum SolButtonSize {
  default = 'default',
  small = 'small',
}

/** Single item in a split-button dropdown. */
export interface SolButtonDropdownItem {
  readonly id: string;
  readonly label: string;
  readonly icon?: SolIcon;
}

export class SolButton {
  private constructor(
    public readonly id: string,
    public readonly type: SolButtonType,
    public readonly icon: SolIcon | undefined,
    public readonly text: string,
    public readonly appendIcon: SolIcon | undefined,
    public readonly disabled: boolean,
    public readonly loading: boolean,
    public readonly custom: boolean,
    public readonly mode: SolButtonMode,
    public readonly size: SolButtonSize,
    public readonly ariaLabel: string | undefined,
    public readonly dropdownItems: readonly SolButtonDropdownItem[],
  ) {}

  public static of(text: string = '', icon: SolIcon | undefined = undefined): SolButton {
    const normalizedText = text ?? '';
    return new SolButton(
      '',
      SolButtonType.secondary,
      icon,
      normalizedText,
      undefined,
      false,
      false,
      false,
      SolButton.calculateMode(normalizedText, false),
      SolButtonSize.default,
      undefined,
      [],
    );
  }

  public static ofExternalLink(text: string): SolButton {
    return SolButton.of(text).withType(SolButtonType.link).withAppendIcon(SolIcon.open_new);
  }

  public withId(id: string): SolButton {
    return this.copy({ id });
  }

  public withType(type: SolButtonType): SolButton {
    return this.copy({ type });
  }

  public withIcon(icon: SolIcon | undefined): SolButton {
    return this.copy({ icon });
  }

  public withAppendIcon(appendIcon: SolIcon | undefined): SolButton {
    return this.copy({ appendIcon });
  }

  public withText(text: string | undefined): SolButton {
    const normalizedText = text ?? '';
    return this.copy({
      text: normalizedText,
      mode: SolButton.calculateMode(normalizedText, this.custom),
    });
  }

  public withDisabled(disabled: boolean): SolButton {
    return this.copy({ disabled });
  }

  public withLoading(loading: boolean): SolButton {
    return this.copy({ loading });
  }

  public withCustom(custom: boolean): SolButton {
    return this.copy({
      custom,
      mode: SolButton.calculateMode(this.text, custom),
    });
  }

  public withSmall(small: boolean): SolButton {
    return this.copy({ size: small ? SolButtonSize.small : SolButtonSize.default });
  }

  public withAriaLabel(ariaLabel: string | undefined): SolButton {
    return this.copy({ ariaLabel });
  }

  public withDropdownItems(dropdownItems: readonly SolButtonDropdownItem[]): SolButton {
    return this.copy({ dropdownItems });
  }

  private copy(props: Partial<SolButton>): SolButton {
    return ObjectTools.copy<SolButton>(this, props);
  }

  private static calculateMode(text: string, custom: boolean): SolButtonMode {
    if (custom) {
      return SolButtonMode.custom;
    }
    return text === '' ? SolButtonMode.icon : SolButtonMode.text;
  }
}
